import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  DateRange as DateRangeIcon,
  Compare as CompareIcon,
  Refresh as RefreshIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useEntityContext } from './EntityContextSwitch';
import api from '../services/api';
import ReportDashboard from './DocumentManagement/ReportDashboard';
import CoachEgoNetwork from './CoachDashboard/CoachEgoNetwork';

// TabPanel component for organizing tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
function Dashboard() {
  console.log('Dashboard component rendering');
  const { user } = useAuth();
  console.log('Dashboard - User:', user?.email);

  const { currentEntity, loading } = useEntityContext();
  console.log('Dashboard - Entity context:', { currentEntity, loading });

  // Tab state with localStorage persistence
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('dashboardSelectedTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  // Date range controls (shared with header)
  const [selectedDateRange, setSelectedDateRange] = useState('30d');
  const [comparisonPeriod, setComparisonPeriod] = useState('previous');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });

  // Report generation state (shared with header button)
  const [generatingReport, setGeneratingReport] = useState(false);
  const [_generationProgress, setGenerationProgress] = useState(null);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    localStorage.setItem('dashboardSelectedTab', newValue.toString());
  };

  // Manual dashboard report generation
  const handleGenerateDashboardReport = async () => {
    if (!currentEntity?.id) {
      alert('Please select an entity first');
      return;
    }

    setGeneratingReport(true);
    setGenerationProgress('Initializing report generation...');

    try {
      const response = await api.generateDashboardReport({
        entityId: currentEntity.id,
        dateRange: selectedDateRange,
        comparisonPeriod: comparisonPeriod,
        customDateRange: selectedDateRange === 'custom' ? customDateRange : null
      });

      if (response?.sessionId) {
        setGenerationProgress('Processing data with AI models...');

        const pollInterval = setInterval(async () => {
          try {
            const progress = await api.getDashboardGenerationProgress(response.sessionId);

            if (progress?.status === 'completed') {
              clearInterval(pollInterval);
              setGenerationProgress('Report generated successfully!');

              setTimeout(() => {
                setGeneratingReport(false);
                setGenerationProgress(null);
              }, 2000);
            } else if (progress?.status === 'failed') {
              clearInterval(pollInterval);
              throw new Error(progress?.error || 'Generation failed');
            } else if (progress?.variableProgress) {
              const completed = progress.variableProgress.completed || 0;
              const total = progress.variableProgress.total || 1;
              const percentage = Math.round((completed / total) * 100);
              setGenerationProgress(`Processing variables: ${completed}/${total} (${percentage}%)`);
            }
          } catch (error) {
            clearInterval(pollInterval);
            throw error;
          }
        }, 2000);

        setTimeout(() => {
          clearInterval(pollInterval);
          if (generatingReport) {
            setGeneratingReport(false);
            setGenerationProgress(null);
            alert('Report generation timed out after 10 minutes. Please try again.');
          }
        }, 600000);
      } else {
        throw new Error('Failed to start report generation');
      }
    } catch (error) {
      console.error('Dashboard generation error:', error);
      setGeneratingReport(false);
      setGenerationProgress(null);
      alert(`Failed to generate dashboard report: ${error.message}`);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              Insights and Reporting
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Welcome back, {user?.displayName || user?.email}
            </Typography>
          </Box>

          {/* Date Range and Comparison Controls */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <DateRangeIcon color="primary" />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={selectedDateRange}
                  onChange={(e) => setSelectedDateRange(e.target.value)}
                  label="Time Period"
                >
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                  <MenuItem value="1y">Last year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>

              {/* Custom Date Range Inputs */}
              {selectedDateRange === 'custom' && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    type="date"
                    label="Start Date"
                    size="small"
                    sx={{ minWidth: 140 }}
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    size="small"
                    sx={{ minWidth: 140 }}
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              )}

              <CompareIcon color="action" />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Compare to</InputLabel>
                <Select
                  value={comparisonPeriod}
                  onChange={(e) => setComparisonPeriod(e.target.value)}
                  label="Compare to"
                >
                  <MenuItem value="previous">Previous period</MenuItem>
                  <MenuItem value="year_ago">Same period last year</MenuItem>
                  <MenuItem value="baseline">Baseline (3 months ago)</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Generate Dashboard Report Button */}
            {currentEntity?.id && (
              <Tooltip
                title={generatingReport ? "Report generation in progress..." : "Generate AI-powered dashboard report for current entity and time period"}
                placement="bottom"
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    onClick={handleGenerateDashboardReport}
                    disabled={generatingReport || !currentEntity?.id}
                    sx={{
                      minWidth: 200,
                      position: 'relative'
                    }}
                  >
                    {generatingReport ? 'Generating...' : 'Generate Dashboard Report'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab icon={<InsightsIcon />} label="Insights" iconPosition="start" />
        <Tab icon={<AssessmentIcon />} label="Reports" iconPosition="start" />
      </Tabs>

      {/* Insights Tab Panel - Coach Ego Network */}
      <TabPanel value={tabValue} index={0}>
        <CoachEgoNetwork
          entityId={currentEntity?.id}
          dateRange={selectedDateRange}
          comparisonPeriod={comparisonPeriod}
          customDateRange={customDateRange}
        />
      </TabPanel>

      {/* Reports Tab Panel */}
      <TabPanel value={tabValue} index={1}>
        <ReportDashboard entityId={currentEntity?.id} />
      </TabPanel>
    </Box>
  );
}

export default Dashboard;
